import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MapPin, LogOut, ChevronDown, Check, Pencil, Heart, User, Home, Map as MapIcon, Building2, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useApp } from "@/contexts/AppContext";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import { LocationDialog } from "@/components/LocationDialog";
import { useBrandLogo } from "@/hooks/useBrandLogo";

// Mobile-nav item descriptor. Centralized so the icon + label + match
// rule all live in one spot. matchPath returns true if the current
// pathname belongs to this nav item (so /map/* still lights up "Map").
const NAV_ITEMS = [
  { to: "/", label: "Home", Icon: Home, match: (p) => p === "/" },
  { to: "/map", label: "Map", Icon: MapIcon, match: (p) => p.startsWith("/map") },
  { to: "/dispensaries", label: "Shops", longLabel: "Dispensaries", Icon: Building2, match: (p) => p.startsWith("/dispensaries") || p.startsWith("/dispensary/") },
  // Commented out per user request (Trulieve-only mode). The "For Shops"
  // marketing tab courts other dispensaries — hidden while we focus on
  // Trulieve. Route + Pricing page kept intact so we can un-hide later.
  // { to: "/pricing", label: "For Shops", longLabel: "For Dispensaries", Icon: Briefcase, match: (p) => p.startsWith("/pricing") || p.startsWith("/onboarding") },
];

export const SiteHeader = () => {
  const { location: appLocation, savedLocations, setLocation, user, logout } = useApp();
  const { user: consumer, favoriteCount, signOut: consumerSignOut } = useConsumerAuth();
  const [locOpen, setLocOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const brand = useBrandLogo();

  const summary = appLocation
    ? appLocation.label
      ? `${appLocation.label} · ${appLocation.city || appLocation.zip}`
      : `${appLocation.city || "PA"}${appLocation.zip ? ` · ${appLocation.zip}` : ""}`
    : "Set location";

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-[hsl(80_7%_9%)] border-b border-[#d4a268]/15 pt-[env(safe-area-inset-top)] shadow-[0_2px_20px_-12px_rgba(0,0,0,0.6)]"
      >
        <div className="container-narrow flex items-center justify-between h-28 sm:h-32 gap-2">
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center group shrink-0"
          >
            <img
              src={brand.src}
              alt={brand.label}
              className="h-20 sm:h-24 w-auto object-contain drop-shadow-[0_0_24px_rgba(212,162,104,0.35)] group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop inline nav — premium gold-underline active state */}
          <nav
            data-testid="header-nav-desktop"
            className="hidden md:flex items-center gap-8 text-sm ml-8 flex-1 min-w-0"
          >
            {NAV_ITEMS.map(({ to, label, longLabel, match }) => {
              const active = match(pathname);
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`header-nav-${to.replace(/\//g, "") || "home"}`}
                  data-active={active}
                  className={`relative inline-flex items-center pb-1 transition-colors font-medium tracking-wide ${
                    active
                      ? "text-foreground"
                      : "text-foreground/55 hover:text-foreground"
                  }`}
                >
                  {longLabel || label}
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-[2px] rounded-full transition-all duration-300 ${
                      active
                        ? "bg-gradient-to-r from-[#d4a268] via-[#e9c896] to-[#d4a268] shadow-[0_0_12px_rgba(212,162,104,0.55)]"
                        : "bg-transparent"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {/* Location quick-switcher dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="header-location-btn"
                  variant="outline"
                  className="rounded-full h-9 border-border bg-transparent text-xs gap-1.5 pl-3 pr-3"
                >
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.6} />
                  <span className="max-w-[140px] truncate">{summary}</span>
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-72"
                data-testid="header-location-menu"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  Search at
                </DropdownMenuLabel>

                {appLocation && (
                  <div className="px-2 py-1.5">
                    <div className="text-xs text-muted-foreground">
                      Currently
                    </div>
                    <div className="text-sm font-medium">
                      {appLocation.label
                        ? `${appLocation.label} · ${appLocation.city || appLocation.zip || ""}`
                        : `${appLocation.city || ""} ${appLocation.zip || ""}`}
                    </div>
                  </div>
                )}

                {savedLocations.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                      Saved
                    </DropdownMenuLabel>
                    {savedLocations.map((s) => (
                      <DropdownMenuItem
                        key={s.id}
                        data-testid={`header-saved-${s.id}`}
                        onClick={() => setLocation(s)}
                        className="flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin
                            className="w-3.5 h-3.5 text-muted-foreground"
                            strokeWidth={1.6}
                          />
                          <span className="font-medium">{s.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {s.zip || s.city}
                          </span>
                        </span>
                        {appLocation?.id === s.id && (
                          <Check className="w-3.5 h-3.5 text-primary" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="header-edit-location"
                  onClick={() => setLocOpen(true)}
                >
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  {savedLocations.length === 0 || !appLocation
                    ? "Set a location"
                    : "Add or change location"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="header-user-menu"
                    variant="ghost"
                    className="rounded-full h-9 px-3 text-xs max-w-[120px] sm:max-w-[180px] truncate"
                  >
                    <span className="truncate">
                      {user.business_name?.slice(0, 14) ||
                        (user.email ? user.email.split("@")[0].slice(0, 12) : "Account")}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    data-testid="header-user-dashboard"
                    onClick={() => navigate("/dispensary/dashboard")}
                  >
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="header-user-billing"
                    onClick={() => navigate("/pricing")}
                  >
                    Billing & Plans
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="header-user-logout"
                    onClick={() => {
                      logout();
                      navigate("/");
                    }}
                  >
                    <LogOut className="w-3.5 h-3.5 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                {/* Saved-favorites quick link — visible to everyone, badge = count */}
                <Link
                  to="/account"
                  data-testid="header-saved-link"
                  className="relative rounded-full h-9 w-9 inline-flex items-center justify-center hover:bg-secondary/60 transition-colors"
                  aria-label="Saved favorites"
                  title={consumer ? "My account" : "Sign in to save across devices"}
                >
                  <Heart className="w-4 h-4" strokeWidth={1.8} />
                  {favoriteCount > 0 && (
                    <span
                      data-testid="header-saved-count"
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold inline-flex items-center justify-center"
                    >
                      {favoriteCount}
                    </span>
                  )}
                </Link>

                {consumer ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-testid="header-consumer-menu"
                        variant="ghost"
                        className="rounded-full h-9 px-3 text-xs max-w-[110px] sm:max-w-[160px]"
                      >
                        <User className="w-3.5 h-3.5 mr-1.5 shrink-0" />
                        <span className="truncate">
                          {consumer.username
                            ? `@${consumer.username}`
                            : (consumer.email?.split("@")[0] || "you").slice(0, 12)}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        data-testid="header-consumer-account"
                        onClick={() => navigate("/account")}
                      >
                        My account
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        data-testid="header-consumer-logout"
                        onClick={() => {
                          consumerSignOut();
                          navigate("/");
                        }}
                      >
                        <LogOut className="w-3.5 h-3.5 mr-2" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    data-testid="header-signin-btn"
                    onClick={() => navigate("/account")}
                    variant="ghost"
                    className="rounded-full h-9 text-xs"
                  >
                    Sign in
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        {/* Mobile-only nav strip — premium gold-accent tab bar. Each tab
            shows its lucide icon + label. Active route gets the brand's
            gold gradient underline (matches desktop nav active state),
            an emerald icon, and a subtle ember glow. No backdrop blur. */}
        <nav
          data-testid="header-nav-mobile"
          className="md:hidden border-t border-[#d4a268]/15 flex items-stretch text-[11px]"
        >
          {NAV_ITEMS.map(({ to, label, Icon, match }) => {
            const active = match(pathname);
            return (
              <Link
                key={to}
                to={to}
                data-testid={`mobile-nav-${to.replace(/\//g, "") || "home"}`}
                data-active={active}
                className={`relative flex-1 flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${
                  active
                    ? "text-foreground"
                    : "text-foreground/50 active:text-foreground/80"
                }`}
              >
                <Icon
                  className={`w-4 h-4 transition-all ${
                    active
                      ? "text-[#7fb594] drop-shadow-[0_0_6px_rgba(127,181,148,0.55)]"
                      : "text-foreground/50"
                  }`}
                  strokeWidth={active ? 2.2 : 1.6}
                />
                <span
                  className={`uppercase tracking-[0.14em] text-[10px] ${
                    active ? "font-semibold" : "font-normal"
                  }`}
                >
                  {label}
                </span>
                {/* gold underline indicator — same gradient as desktop */}
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full transition-all duration-300 ${
                    active
                      ? "w-10 bg-gradient-to-r from-[#d4a268] via-[#e9c896] to-[#d4a268] shadow-[0_0_10px_rgba(212,162,104,0.6)]"
                      : "w-0 bg-transparent"
                  }`}
                />
              </Link>
            );
          })}
        </nav>
      </header>
      <LocationDialog open={locOpen} onOpenChange={setLocOpen} />
    </>
  );
};
